import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { supabase } from '../lib/supabase';
import { JournalEntry } from '../types';

interface JournalState {
  entries: JournalEntry[];
  isLoading: boolean;
  error: string | null;
  actions: {
    fetchEntries: () => Promise<void>;
    addEntry: (entry: Omit<JournalEntry, 'id' | 'user_id' | 'created_at' | 'updated_at'>) => Promise<void>;
    updateEntry: (id: string, updates: Partial<JournalEntry>) => Promise<void>;
    deleteEntry: (id: string) => Promise<void>;
  };
}

export const useJournalStore = create<JournalState>()(
  devtools(
    (set, get) => ({
      entries: [],
      isLoading: false,
      error: null,
      actions: {
        fetchEntries: async () => {
          set({ isLoading: true, error: null });
          try {
            const { data: session } = await supabase.auth.getSession();
            if (!session?.session?.user) {
              set({ entries: [], isLoading: false });
              return;
            }

            const { data, error } = await supabase
              .from('journal_entries')
              .select('*')
              .eq('user_id', session.session.user.id)
              .order('created_at', { ascending: false });

            if (error) throw error;
            
            // Ensure all entries have a template_type
            const entriesWithTemplate = (data || []).map(entry => ({
              ...entry,
              template_type: entry.template_type || 'quick'
            }));
            
            set({ entries: entriesWithTemplate });
          } catch (err) {
            console.error('Error fetching entries:', err);
            set({ error: err instanceof Error ? err.message : 'Failed to fetch entries' });
          } finally {
            set({ isLoading: false });
          }
        },

        addEntry: async (entry) => {
          set({ isLoading: true, error: null });
          try {
            const { data: session } = await supabase.auth.getSession();
            if (!session?.session?.user) {
              throw new Error('Not authenticated');
            }

            const { data, error } = await supabase
              .from('journal_entries')
              .insert([{
                ...entry,
                user_id: session.session.user.id,
                template_type: entry.template_type || 'quick'
              }])
              .select()
              .single();

            if (error) throw error;
            
            const currentEntries = get().entries;
            set({ 
              entries: [data, ...currentEntries],
              error: null
            });
          } catch (err) {
            console.error('Error adding entry:', err);
            set({ error: err instanceof Error ? err.message : 'Failed to add entry' });
            throw err;
          } finally {
            set({ isLoading: false });
          }
        },

        updateEntry: async (id, updates) => {
          set({ isLoading: true, error: null });
          try {
            const { data: session } = await supabase.auth.getSession();
            if (!session?.session?.user) {
              throw new Error('Not authenticated');
            }

            const { data, error } = await supabase
              .from('journal_entries')
              .update(updates)
              .eq('id', id)
              .eq('user_id', session.session.user.id)
              .select()
              .single();

            if (error) throw error;

            const currentEntries = get().entries;
            set({
              entries: currentEntries.map(e => e.id === id ? { ...e, ...data } : e),
              error: null
            });
          } catch (err) {
            console.error('Error updating entry:', err);
            set({ error: err instanceof Error ? err.message : 'Failed to update entry' });
            throw err;
          } finally {
            set({ isLoading: false });
          }
        },

        deleteEntry: async (id) => {
          set({ isLoading: true, error: null });
          try {
            const { data: session } = await supabase.auth.getSession();
            if (!session?.session?.user) {
              throw new Error('Not authenticated');
            }

            const { error } = await supabase
              .from('journal_entries')
              .delete()
              .eq('id', id)
              .eq('user_id', session.session.user.id);

            if (error) throw error;

            const currentEntries = get().entries;
            set({
              entries: currentEntries.filter(e => e.id !== id),
              error: null
            });
          } catch (err) {
            console.error('Error deleting entry:', err);
            set({ error: err instanceof Error ? err.message : 'Failed to delete entry' });
            throw err;
          } finally {
            set({ isLoading: false });
          }
        }
      }
    })
  )
);